import React, { useState, useEffect, useRef } from 'react';
import { MoonStar, CalendarDays, BarChart3, TrendingUp } from 'lucide-react';
import { syncStatusBar, tapFeedback } from './utils/native';
import { Header } from './components/Header';
import { PrayerList } from './components/PrayerList';
import { Calendar } from './components/Calendar';
import { Statistics } from './components/Statistics';
import { LocationSettings } from './components/LocationSettings';
import { NotificationSettings } from './components/NotificationSettings';
import { Visualization } from './components/Visualization';
import { PWAPrompt } from './components/PWAPrompt';
import { useAuth } from './hooks/useAuth';
import { useFirestore, getLocalDateString } from './hooks/useFirestore';
import { calculatePrayerTimes, getCurrentLocation } from './utils/prayerTimes';
import { DEFAULT_NOTIF_PREFS, schedulePrayerNotifications, cancelPrayerNotifications, type NotificationPrefs } from './utils/notifications';
import { getThemeColors } from './utils/themeUtils';
import type { Prayer, DailyPrayers, CalendarDay, UserLocation, Language, Theme } from './types';

export function App() {
  const { user, loading: authLoading } = useAuth();
  const { saveUserData, loadUserData, loadPrayerHistory, loadPrayersForDate, forceSave } = useFirestore(user?.uid || null);
  
  const [language, setLanguage] = useState<Language>('en');
  const [theme, setTheme] = useState<Theme>('emerald');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState<'prayers' | 'calendar' | 'stats' | 'visualization'>('prayers');
  const [prayers, setPrayers] = useState<Prayer[]>([
    { 
      name: 'Fajr', 
      scheduledTime: '04:30', 
      actualTime: null, 
      completed: false, 
      location: '',
      prayerStatus: 'individual',
      travelStatus: 'mukim',
      healthStatus: 'sehat',
      timeStatus: 'ontime'
    },
    { 
      name: 'Dhuhr', 
      scheduledTime: '12:00', 
      actualTime: null, 
      completed: false, 
      location: '',
      prayerStatus: 'individual',
      travelStatus: 'mukim',
      healthStatus: 'sehat',
      timeStatus: 'ontime'
    },
    { 
      name: 'Asr', 
      scheduledTime: '15:30', 
      actualTime: null, 
      completed: false, 
      location: '',
      prayerStatus: 'individual',
      travelStatus: 'mukim',
      healthStatus: 'sehat',
      timeStatus: 'ontime'
    },
    { 
      name: 'Maghrib', 
      scheduledTime: '18:00', 
      actualTime: null, 
      completed: false, 
      location: '',
      prayerStatus: 'individual',
      travelStatus: 'mukim',
      healthStatus: 'sehat',
      timeStatus: 'ontime'
    },
    { 
      name: 'Isha', 
      scheduledTime: '19:30', 
      actualTime: null, 
      completed: false, 
      location: '',
      prayerStatus: 'individual',
      travelStatus: 'mukim',
      healthStatus: 'sehat',
      timeStatus: 'ontime'
    }
  ]);
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isDark, setIsDark] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [prayerHistory, setPrayerHistory] = useState<DailyPrayers[]>([]);
  const [userLocation, setUserLocation] = useState<UserLocation | undefined>();
  const [isSyncing, setIsSyncing] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs>(() => {
    try {
      const raw = localStorage.getItem('notificationPrefs');
      if (raw) return { ...DEFAULT_NOTIF_PREFS, ...JSON.parse(raw) };
    } catch { /* abaikan */ }
    return DEFAULT_NOTIF_PREFS;
  });
  const [notifError, setNotifError] = useState<string | null>(null);
  
  // State for save functionality - manual save with dedicated button
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Prevent dirty check during initial load
  const isLoadingPrayersRef = useRef(false);
  const lastSavedPrayersRef = useRef<string>('');
  const historyUnsubscribeRef = useRef<(() => void) | null>(null);
  const hasMigratedLocalDataRef = useRef(false);

  // Samakan status bar native (Android/iOS) dengan dark mode
  useEffect(() => {
    syncStatusBar(isDark);
  }, [isDark]);

  const isToday = selectedDate.toDateString() === new Date().toDateString();
  const themeColors = getThemeColors(theme);
  const selectedDateString = getLocalDateString(selectedDate);

  // Auto-get location on first load
  useEffect(() => {
    const getInitialLocation = async () => {
      try {
        const location = await getCurrentLocation();
        setUserLocation(location);
      } catch (error) {
        console.log('Could not get initial location:', error);
      }
    };

    if (!userLocation) {
      getInitialLocation();
    }
  }, []);

  // Reset load flag when user changes (login/logout) - critical to prevent auto-save overwrite
  useEffect(() => {
    if (!authLoading) {
      setInitialDataLoaded(false);
      isLoadingPrayersRef.current = true;
    }
    // Cleanup previous history listener when user changes
    if (historyUnsubscribeRef.current) {
      historyUnsubscribeRef.current();
      historyUnsubscribeRef.current = null;
    }
    if (!user) {
      hasMigratedLocalDataRef.current = false;
    }
  }, [user?.uid, authLoading]);

  // Load initial data when app starts
  useEffect(() => {
    if (authLoading) return;

    let cancelled = false;

    const loadInitialData = async () => {
      try {
        if (user) {
          // Load data from Firestore for authenticated users
          const userData = await loadUserData();
          if (cancelled) return;
          if (userData) {
            if (userData.userLocation) setUserLocation(userData.userLocation);
            if (userData.language) setLanguage(userData.language as Language);
            if (userData.theme) setTheme(userData.theme as Theme);
            if (userData.notificationPrefs) setNotifPrefs({ ...DEFAULT_NOTIF_PREFS, ...userData.notificationPrefs });
            // Always load today's prayers if exists (no isToday gate) - prevents data loss after login
            if (userData.todayPrayers && userData.todayPrayers.length > 0) {
              // Only set if currently viewing today, otherwise history loader will handle selected date
              const todayStr = getLocalDateString();
              if (selectedDateString === todayStr) {
                isLoadingPrayersRef.current = true;
                setPrayers(userData.todayPrayers);
                console.log('Loaded today prayers from Firestore:', userData.todayPrayers);
              }
            }
          }

          // Set up real-time prayer history listener with proper cleanup
          const unsubscribe = loadPrayerHistory((history) => {
            if (cancelled) return;
            setPrayerHistory(history);

            // Migrate localStorage data to Firestore on first login if Firestore is empty
            if (!hasMigratedLocalDataRef.current && history.length === 0) {
              const savedHistory = localStorage.getItem('prayerHistory');
              if (savedHistory) {
                try {
                  const localHistory: DailyPrayers[] = JSON.parse(savedHistory);
                  if (localHistory.length > 0) {
                    hasMigratedLocalDataRef.current = true;
                    console.log('Migrating localStorage history to Firestore:', localHistory.length);
                    // Fire-and-forget migration (don't block UI)
                    localHistory.forEach(day => {
                      forceSave(day.prayers, day.date).catch(e => console.error('Migration failed for', day.date, e));
                    });
                  }
                } catch (e) { console.error('Migration parse error', e); }
              }
            }
          });
          historyUnsubscribeRef.current = unsubscribe;
          setInitialDataLoaded(true);
          setTimeout(() => { isLoadingPrayersRef.current = false; }, 500);
        } else {
          // Load data from localStorage for offline users
          const savedHistory = localStorage.getItem('prayerHistory');
          if (savedHistory) {
            const history: DailyPrayers[] = JSON.parse(savedHistory);
            if (!cancelled) setPrayerHistory(history);
            
            // Load prayers for selected date from localStorage
            const dayData = history.find(day => day.date === selectedDateString);
            if (dayData && dayData.prayers && dayData.prayers.length > 0) {
              isLoadingPrayersRef.current = true;
              setPrayers(dayData.prayers);
              console.log('Loaded prayers from localStorage for', selectedDateString, dayData.prayers);
              setTimeout(() => { isLoadingPrayersRef.current = false; }, 300);
            }
          }

          const savedLocation = localStorage.getItem('userLocation');
          if (savedLocation) {
            setUserLocation(JSON.parse(savedLocation));
          }

          const savedLanguage = localStorage.getItem('preferredLanguage');
          if (savedLanguage) {
            setLanguage(savedLanguage as Language);
          }

          const savedTheme = localStorage.getItem('preferredTheme');
          if (savedTheme) {
            setTheme(savedTheme as Theme);
          }

          setInitialDataLoaded(true);
          setTimeout(() => { isLoadingPrayersRef.current = false; }, 300);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        if (!cancelled) setInitialDataLoaded(true);
        isLoadingPrayersRef.current = false;
      }
    };

    loadInitialData();

    return () => {
      cancelled = true;
      if (historyUnsubscribeRef.current) {
        historyUnsubscribeRef.current();
        historyUnsubscribeRef.current = null;
      }
    };
  }, [user, authLoading, loadUserData, loadPrayerHistory, forceSave, selectedDateString]);

  // Load prayers for selected date (both today and historical)
  useEffect(() => {
    const loadPrayersForSelectedDate = async () => {
      if (!initialDataLoaded || authLoading) return;
      // Don't reload if we're still in initial loading phase
      if (isLoadingPrayersRef.current && isToday) return;

      const dateString = selectedDateString;
      
      if (isToday) {
        // For today, try to find in prayerHistory first (from real-time listener)
        const todayData = prayerHistory.find(day => day.date === dateString);
        if (todayData && todayData.prayers && todayData.prayers.length > 0) {
          // Avoid overwriting if prayers already matches history (prevent loop)
          const isSame = JSON.stringify(todayData.prayers) === JSON.stringify(prayers);
          if (!isSame) {
            isLoadingPrayersRef.current = true;
            setPrayers(todayData.prayers);
            console.log('Loaded today prayers from history for', dateString);
            setTimeout(() => { isLoadingPrayersRef.current = false; }, 300);
          }
        }
        // If no history yet, keep current prayers (already loaded from loadUserData)
        return;
      }

      // Historical date
      isLoadingPrayersRef.current = true;
      if (user) {
        const historicalPrayers = await loadPrayersForDate(dateString);
        if (historicalPrayers && historicalPrayers.length > 0) {
          setPrayers(historicalPrayers);
          console.log('Loaded historical prayers from Firestore for', dateString, historicalPrayers);
        } else {
          const defaultPrayers = getDefaultPrayers();
          setPrayers(defaultPrayers);
        }
      } else {
        const dayData = prayerHistory.find(day => day.date === dateString);
        if (dayData && dayData.prayers && dayData.prayers.length > 0) {
          setPrayers(dayData.prayers);
          console.log('Loaded historical prayers from localStorage for', dateString, dayData.prayers);
        } else {
          const defaultPrayers = getDefaultPrayers();
          setPrayers(defaultPrayers);
        }
      }
      setTimeout(() => { isLoadingPrayersRef.current = false; }, 300);
    };

    loadPrayersForSelectedDate();
  }, [selectedDateString, user, isToday, loadPrayersForDate, initialDataLoaded, authLoading, prayerHistory]);

  // Manual save mode: track dirty state instead of auto-save
  useEffect(() => {
    if (!initialDataLoaded) return;
    // When we are in loading phase, sync baseline to avoid false dirty
    if (isLoadingPrayersRef.current) {
      lastSavedPrayersRef.current = JSON.stringify(prayers);
      setHasUnsavedChanges(false);
      return;
    }
    const current = JSON.stringify(prayers);
    // First baseline initialization
    if (!lastSavedPrayersRef.current) {
      lastSavedPrayersRef.current = current;
      setHasUnsavedChanges(false);
      return;
    }
    setHasUnsavedChanges(current !== lastSavedPrayersRef.current);
  }, [prayers, initialDataLoaded]);

  // Keep baseline in sync when selected date changes and loading finishes
  useEffect(() => {
    if (!initialDataLoaded || isLoadingPrayersRef.current) return;
    // Small delay to let prayer state settle after date load
    const t = setTimeout(() => {
      if (!hasUnsavedChanges) {
        lastSavedPrayersRef.current = JSON.stringify(prayers);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [selectedDateString, initialDataLoaded]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // Save to localStorage for offline users when data changes
  useEffect(() => {
    if (!user && initialDataLoaded) {
      localStorage.setItem('prayerHistory', JSON.stringify(prayerHistory));
    }
  }, [prayerHistory, user, initialDataLoaded]);

  useEffect(() => {
    if (!user && userLocation && initialDataLoaded) {
      localStorage.setItem('userLocation', JSON.stringify(userLocation));
    }
  }, [userLocation, user, initialDataLoaded]);

  useEffect(() => {
    if (!user && initialDataLoaded) {
      localStorage.setItem('preferredLanguage', language);
    }
  }, [language, user, initialDataLoaded]);

  useEffect(() => {
    if (!user && initialDataLoaded) {
      localStorage.setItem('preferredTheme', theme);
    }
  }, [theme, user, initialDataLoaded]);

  // Persist notification prefs locally (always) - Firestore via manual Save
  useEffect(() => {
    if (initialDataLoaded) {
      localStorage.setItem('notificationPrefs', JSON.stringify(notifPrefs));
    }
  }, [notifPrefs, initialDataLoaded]);

  // Reschedule adhan reminders when schedule/prefs change
  useEffect(() => {
    if (!initialDataLoaded) return;
    if (!notifPrefs.enabled) {
      cancelPrayerNotifications();
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        await schedulePrayerNotifications(prayers, notifPrefs);
        if (!cancelled) setNotifError(null);
      } catch (e) {
        if (!cancelled) setNotifError(e instanceof Error ? e.message : 'Gagal menjadwalkan notifikasi');
      }
    }, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [prayers, notifPrefs, initialDataLoaded, selectedDateString]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (userLocation && initialDataLoaded) {
      const prayerTimes = calculatePrayerTimes(userLocation);
      // Use functional update to avoid stale closure overwriting completed status
      setPrayers(prev => prev.map((prayer, index) => {
        const timeMap = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'] as const;
        const newTime = prayerTimes[timeMap[index]];
        // Only update if time actually changed to avoid unnecessary saves
        if (prayer.scheduledTime === newTime) return prayer;
        return {
          ...prayer,
          scheduledTime: newTime
        };
      }));
    }
  }, [userLocation, initialDataLoaded]);

  const getDefaultPrayers = (): Prayer[] => [
    { 
      name: 'Fajr', 
      scheduledTime: '04:30', 
      actualTime: null, 
      completed: false, 
      location: '',
      prayerStatus: 'individual',
      travelStatus: 'mukim',
      healthStatus: 'sehat',
      timeStatus: 'ontime'
    },
    { 
      name: 'Dhuhr', 
      scheduledTime: '12:00', 
      actualTime: null, 
      completed: false, 
      location: '',
      prayerStatus: 'individual',
      travelStatus: 'mukim',
      healthStatus: 'sehat',
      timeStatus: 'ontime'
    },
    { 
      name: 'Asr', 
      scheduledTime: '15:30', 
      actualTime: null, 
      completed: false, 
      location: '',
      prayerStatus: 'individual',
      travelStatus: 'mukim',
      healthStatus: 'sehat',
      timeStatus: 'ontime'
    },
    { 
      name: 'Maghrib', 
      scheduledTime: '18:00', 
      actualTime: null, 
      completed: false, 
      location: '',
      prayerStatus: 'individual',
      travelStatus: 'mukim',
      healthStatus: 'sehat',
      timeStatus: 'ontime'
    },
    { 
      name: 'Isha', 
      scheduledTime: '19:30', 
      actualTime: null, 
      completed: false, 
      location: '',
      prayerStatus: 'individual',
      travelStatus: 'mukim',
      healthStatus: 'sehat',
      timeStatus: 'ontime'
    }
  ];

  const togglePrayer = (index: number) => {
    tapFeedback();
    const newPrayers = [...prayers];
    const now = new Date();
    const timeString = now.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    
    newPrayers[index] = {
      ...newPrayers[index],
      completed: !newPrayers[index].completed,
      actualTime: !newPrayers[index].completed ? timeString : null
    };
    
    console.log('Prayer toggled:', newPrayers[index].name, 'completed:', newPrayers[index].completed);
    setPrayers(newPrayers);
  };

  const updateLocation = (index: number, location: string) => {
    const newPrayers = [...prayers];
    newPrayers[index].location = location;
    setPrayers(newPrayers);
  };

  const updateActualTime = (index: number, time: string) => {
    const newPrayers = [...prayers];
    newPrayers[index].actualTime = time;
    setPrayers(newPrayers);
  };

  const updatePrayer = (index: number, updates: Partial<Prayer>) => {
    const newPrayers = [...prayers];
    newPrayers[index] = { ...newPrayers[index], ...updates };
    setPrayers(newPrayers);
  };

  // Flush save immediately before unload/logout - prevents debounce loss
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (user && prayers.length > 0 && initialDataLoaded && !isLoadingPrayersRef.current) {
        // Use sendBeacon-like sync: store in localStorage as backup
        try {
          const backupKey = `pendingSave-${user.uid}-${selectedDateString}`;
          localStorage.setItem(backupKey, JSON.stringify(prayers));
        } catch {}
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user, prayers, selectedDateString, initialDataLoaded]);

  const handleSave = async () => {
    setIsSyncing(true);
    setSaveError(null);
    try {
      const dateString = selectedDateString;
      
      if (user) {
        // Always save with explicit date to avoid UTC/today ambiguity
        await saveUserData({
          prayers,
          userLocation,
          language,
          theme,
          date: dateString
        });
        // Also save preferences separately when saving today
        if (isToday) {
          await saveUserData({
            prayers,
            userLocation,
            language,
            theme,
            notificationPrefs: notifPrefs,
            date: undefined
          }).catch(()=>{});
        }
        console.log('Manual save to Firestore completed for', dateString);
      } else {
        // Save to localStorage for offline users
        const updatedHistory = [...prayerHistory];
        const dayIndex = updatedHistory.findIndex(day => day.date === dateString);

        if (dayIndex >= 0) {
          updatedHistory[dayIndex] = { date: dateString, prayers };
        } else {
          updatedHistory.push({ date: dateString, prayers });
        }

        setPrayerHistory(updatedHistory);
        localStorage.setItem('prayerHistory', JSON.stringify(updatedHistory));
        console.log('Manual save to localStorage completed for', dateString);
      }
      // Update baseline -> no longer dirty
      lastSavedPrayersRef.current = JSON.stringify(prayers);
      setHasUnsavedChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (error: unknown) {
      console.error('Failed to save data:', error);
      const msg = error instanceof Error ? error.message : 'Gagal menyimpan';
      setSaveError(msg);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLocationUpdate = (location: UserLocation) => {
    setUserLocation(location);
  };

  const handleCalendarDateSelect = (date: Date) => {
    setSelectedDate(date);
    setActiveTab('prayers');
  };

  const getCalendarData = (): CalendarDay[] => {
    return prayerHistory.map(day => ({
      date: day.date,
      completionRate: day.prayers.filter(p => p.completed).length / day.prayers.length
    }));
  };

  // Show loading only during initial auth check, not during data loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div 
            className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
            style={{ borderColor: themeColors[500], borderTopColor: 'transparent' }}
          />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <Header
        currentTime={currentTime}
        isDark={isDark}
        setIsDark={setIsDark}
        language={language}
        onLanguageChange={setLanguage}
        theme={theme}
        onThemeChange={setTheme}
        userLocation={userLocation}
      />

      <main className="container mx-auto px-4 py-4 sm:py-8 pb-28 sm:pb-8">
        <div className="max-w-4xl mx-auto">
          {/* Navigation Tabs - desktop / tablet */}
          <div className={`hidden sm:block rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'} p-2 mb-6`}>
            <div className="flex flex-wrap gap-1">
              {[
                { id: 'prayers', label: 'Prayers', icon: '🕌' },
                { id: 'calendar', label: 'Calendar', icon: '📅' },
                { id: 'stats', label: 'Statistics', icon: '📊' },
                { id: 'visualization', label: 'Analytics', icon: '📈' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors flex-1 sm:flex-none justify-center ${
                    activeTab === tab.id
                      ? 'text-white'
                      : isDark
                      ? 'text-gray-300 hover:bg-gray-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  style={activeTab === tab.id ? { backgroundColor: themeColors[600] } : {}}
                >
                  <span className="text-base">{tab.icon}</span>
                  <span className="text-base font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'prayers' && (
            <>
              {/* Date Navigation for non-today dates */}
              {!isToday && (
                <div className={`rounded-xl shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'} p-4 sm:p-6 mb-6 sm:mb-8`}>
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => {
                        const prevDay = new Date(selectedDate);
                        prevDay.setDate(prevDay.getDate() - 1);
                        setSelectedDate(prevDay);
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      }`}
                    >
                      ←
                    </button>
                    
                    <div className="text-center">
                      <h2 className="text-lg font-semibold">
                        {selectedDate.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </h2>
                    </div>
                    
                    <button
                      onClick={() => {
                        const nextDay = new Date(selectedDate);
                        nextDay.setDate(nextDay.getDate() + 1);
                        if (nextDay <= new Date()) {
                          setSelectedDate(nextDay);
                        }
                      }}
                      disabled={selectedDate >= new Date()}
                      className={`p-2 rounded-lg transition-colors ${
                        selectedDate >= new Date()
                          ? 'opacity-50 cursor-not-allowed'
                          : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      }`}
                    >
                      →
                    </button>
                  </div>
                  
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setSelectedDate(new Date())}
                      className="px-4 py-2 rounded-lg text-white transition-colors"
                      style={{ backgroundColor: themeColors[600] }}
                    >
                      Go to Today
                    </button>
                  </div>
                </div>
              )}

              {/* Location Settings - only show if no location or user wants to update */}
              {!userLocation && (
                <LocationSettings
                  isDark={isDark}
                  onLocationUpdate={handleLocationUpdate}
                  currentLocation={userLocation}
                  language={language}
                  theme={theme}
                />
              )}

              <NotificationSettings
                isDark={isDark}
                theme={theme}
                language={language}
                prefs={notifPrefs}
                onChange={setNotifPrefs}
              />
              {notifError && (
                <p className="text-amber-600 text-sm mb-4">⚠️ {notifError}</p>
              )}

              <PrayerList
                prayers={prayers}
                isDark={isDark}
                onTogglePrayer={togglePrayer}
                onUpdateLocation={updateLocation}
                onUpdateActualTime={updateActualTime}
                onUpdatePrayer={updatePrayer}
                language={language}
                selectedDate={selectedDate}
                isToday={isToday}
                theme={theme}
                hasUnsavedChanges={hasUnsavedChanges}
                isSyncing={isSyncing}
                onSave={handleSave}
                saveSuccess={saveSuccess}
                saveError={saveError}
              />
            </>
          )}

          {activeTab === 'calendar' && (
            <Calendar
              selectedMonth={selectedMonth}
              calendarData={getCalendarData()}
              isDark={isDark}
              onMonthChange={setSelectedMonth}
              onDateSelect={handleCalendarDateSelect}
              theme={theme}
            />
          )}

          {activeTab === 'stats' && (
            <Statistics
              completedCount={prayers.filter(p => p.completed).length}
              totalCount={prayers.length}
              isDark={isDark}
              language={language}
              prayerHistory={prayerHistory}
              selectedDate={selectedDate}
              isToday={isToday}
              theme={theme}
            />
          )}

          {activeTab === 'visualization' && (
            <Visualization
              isDark={isDark}
              language={language}
              prayerHistory={prayerHistory}
              theme={theme}
            />
          )}
        </div>
      </main>

      {/* Bottom navigation - mobile only, thumb-friendly */}
      <nav
        className={`sm:hidden fixed bottom-0 left-0 right-0 z-40 border-t pb-safe ${
          isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 border-gray-200'
        } backdrop-blur supports-[backdrop-filter]:bg-opacity-90`}
        aria-label="Navigasi utama"
      >
        <div className="grid grid-cols-4 gap-1 px-2 pt-2">
          {[
            { id: 'prayers', label: 'Sholat', Icon: MoonStar },
            { id: 'calendar', label: 'Kalender', Icon: CalendarDays },
            { id: 'stats', label: 'Statistik', Icon: BarChart3 },
            { id: 'visualization', label: 'Analitik', Icon: TrendingUp },
          ].map(({ id, label, Icon }) => {
            const active = activeTab === id;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id as any)}
                aria-current={active ? 'page' : undefined}
                className={`flex flex-col items-center justify-center gap-1 rounded-xl py-2.5 text-xs font-medium transition-colors ${
                  active
                    ? 'text-white'
                    : isDark
                    ? 'text-gray-300 active:bg-gray-700'
                    : 'text-gray-600 active:bg-gray-100'
                }`}
                style={active ? { backgroundColor: themeColors[600] } : {}}
              >
                <Icon className="w-6 h-6" strokeWidth={active ? 2.5 : 2} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <PWAPrompt isDark={isDark} />
    </div>
  );
}